import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { ChangePinDialog } from '../../components/ChangePinDialog';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { 
  LayoutDashboard, Package, Tags, ClipboardList, 
  BarChart3, LogOut, ShoppingCart, Settings, Users,
  FileText, Receipt, Award, Sparkles, KeyRound,
  Maximize, Minimize, Menu
} from 'lucide-react';

const allNavItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true, roles: ['admin', 'chef'] },
  { to: '/admin/products', icon: Package, label: 'Products', roles: ['admin', 'chef'] },
  { to: '/admin/categories', icon: Tags, label: 'Categories', roles: ['admin', 'chef'] },
  { to: '/admin/users', icon: Users, label: 'Users', roles: ['admin'] },
  { to: '/admin/customers', icon: Award, label: 'Customers', roles: ['admin', 'chef'] },
  { to: '/admin/loyalty', icon: Sparkles, label: 'Loyalty', roles: ['admin'] },
  { to: '/admin/restock', icon: ClipboardList, label: 'Restock', roles: ['admin', 'chef'] },
  { to: '/admin/transactions', icon: Receipt, label: 'Transactions', roles: ['admin'] },
  { to: '/admin/zreport', icon: FileText, label: 'Z-Report', roles: ['admin'] },
  { to: '/admin/reports', icon: BarChart3, label: 'Reports', roles: ['admin'] },
  { to: '/admin/settings', icon: Settings, label: 'Settings', roles: ['admin'] },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || 'operator';
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = allNavItems.filter(item => item.roles.includes(role));

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen?.() ||
      document.documentElement.webkitRequestFullscreen?.();
    }
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };

  const SidebarContent = ({ onNavClick }) => (
    <>
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold">Kasseport</h1>
        <p className="text-sm text-white/60 mt-1">
          {role === 'chef' ? 'Kitchen Panel' : 'Admin Panel'}
        </p>
      </div>

      <nav className="space-y-1 flex-1">
        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}
            data-testid={`nav-${item.label.toLowerCase()}`}
            onClick={onNavClick}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <item.icon className="w-5 h-5" />
            <span className="font-body">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto pt-8 border-t border-white/10">
        <Button variant="ghost" data-testid="till-link"
          onClick={() => { navigate('/till'); onNavClick?.(); }}
          className="nav-item w-full justify-start text-white/70 hover:text-white hover:bg-white/10">
          <ShoppingCart className="w-5 h-5" /><span>Go to Till</span>
        </Button>

        {role === 'admin' && (
          <Button variant="ghost" data-testid="admin-fullscreen-btn" onClick={toggleFullscreen}
            className="nav-item w-full justify-start text-white/70 hover:text-white hover:bg-white/10 mt-1">
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            <span>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
          </Button>
        )}

        <div className="mt-4 px-4">
          <p className="text-sm text-white/60 font-body">{user?.username}</p>
          <p className="text-xs text-white/40 capitalize">{role}{user?.user_code ? ` · #${user.user_code}` : ''}</p>
        </div>

        <Button variant="ghost" data-testid="admin-change-pin-btn" onClick={() => setShowPinDialog(true)}
          className="nav-item w-full justify-start text-white/70 hover:text-white hover:bg-white/10 mt-2">
          <KeyRound className="w-5 h-5" /><span>Change PIN</span>
        </Button>

        <Button variant="ghost" data-testid="admin-logout-btn" onClick={handleLogout}
          className="nav-item w-full justify-start text-white/70 hover:text-white hover:bg-white/10 mt-1">
          <LogOut className="w-5 h-5" /><span>Logout</span>
        </Button>
      </div>
    </>
  );

  return (
    <div className="admin-layout" data-testid="admin-layout">
      {/* Desktop sidebar - hidden on tablet portrait */}
      <aside className="admin-sidebar hidden lg:flex flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile/tablet header with hamburger */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-slate-900 text-white sticky top-0 z-30">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="admin-menu-btn">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0 bg-slate-900 border-slate-700 text-white [&>button]:text-white">
            <div className="flex flex-col h-full p-4">
              <SidebarContent onNavClick={() => setMobileOpen(false)} />
            </div>
          </SheetContent>
        </Sheet>
        <h1 className="font-heading text-lg font-bold">Kasseport</h1>
        <div className="flex items-center gap-2">
          {role === 'admin' && (
            <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="text-white hover:bg-white/10" data-testid="admin-fullscreen-btn-mobile">
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </Button>
          )}
          <span className="text-xs text-white/60">{user?.username}</span>
        </div>
      </div>

      <main className="admin-content">
        <Outlet />
      </main>
      
      <ChangePinDialog open={showPinDialog} onOpenChange={setShowPinDialog} />
    </div>
  );
};

export default AdminLayout;
